import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet } from 'react-native';
import { RefreshControl } from 'react-native-gesture-handler';

import { RouteProp, useRoute, useScrollToTop } from '@react-navigation/native';

import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { Column } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { Links } from '@/feature/account-details/components/Links';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useActiveProductName } from '@/hooks/use-active-product-name';
import { useActiveWallet } from '@/hooks/use-active-wallet';
import { useAvailableProducts } from '@/hooks/use-available-products';
import { useForceRefresh } from '@/hooks/use-force-refresh';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useGetAuthUserTrustlyInfo } from '@/hooks/use-user-trustly-information';
import { useWallet } from '@/hooks/use-wallet';
import { AccountStackParamList } from '@/navigation/types';
import { LoadingScreen } from '@/screens/LoadingScreen';
import { common, designSystem } from '@/styles/styles';
import { ProductDocumentDataBodyScreenLinksSliceItem } from '@/types/prismic.generated';
import { showScrollIndicator } from '@/utils/constants-platform-specific';

import { AccountFooter } from '../components/AccountFooter';
import { SecondaryWalletButton } from '../components/wallet-card/SecondaryWalletButton';
import { WalletActionButtons } from '../components/wallet-card/WalletActionButtons';
import { WalletCards } from '../components/wallet-card/WalletCards';
import { buildCardItems, getCurrenciesForProduct } from '../components/wallet-card/utils';
import { getSharedWalletSubtitle } from '../utils/utils';

export type AccountRouteProp = RouteProp<AccountStackParamList, 'Account'>;

const EMPTY: ProductDocumentDataBodyScreenLinksSliceItem[] = [];

export const AccountScreen = () => {
    const { t } = useTranslation(['account', 'wallet', 'common']);

    const { params } = useRoute<AccountRouteProp>();
    const hasSocialWallet = useHasSocialWallet();
    const { isProductAvailable } = useAvailableProducts();
    const activeProductName = useActiveProductName();
    const activeProduct = useJurisdictionStore(state => state.product);
    const allLinks = useJurisdictionStore(state => state.jurisdictionSettings?.productConfig?.screenLinks) ?? EMPTY;

    // If already Trustly verified, filter linking bank link from ACS
    const { data: trustlyInfo } = useGetAuthUserTrustlyInfo();
    const links = trustlyInfo?.TRUSTLY_VERIFIED ? allLinks.filter(link => link.screen !== 'TrustlyId') : allLinks;
    const activeWallet = useActiveWallet();

    const { actions, walletLoading, wallet } = useWallet();
    const { forceRefreshing, onRefresh } = useForceRefresh(actions.loadUserWallet);

    const listRef = useRef<ScrollView>(null);
    useScrollToTop(listRef);

    const cards = useMemo(() => {
        const currencies = getCurrenciesForProduct(activeProduct);
        return buildCardItems(currencies, activeWallet.wallets[activeProduct], activeProduct);
    }, [activeProduct, activeWallet]);

    const sharedWalletSubtitle = useMemo(
        () => getSharedWalletSubtitle(activeProduct, isProductAvailable, t),
        [activeProduct, isProductAvailable, t]
    );

    if (walletLoading && !wallet) {
        return <LoadingScreen />;
    }

    return (
        <Screen>
            <ScrollView
                ref={listRef}
                scrollEventThrottle={16}
                showsVerticalScrollIndicator={showScrollIndicator}
                contentContainerStyle={[common.grow]}
                refreshControl={
                    <RefreshControl
                        enabled
                        colors={[designSystem.colors.neutral700]}
                        tintColor={ds.colorValue.icon.primary}
                        refreshing={forceRefreshing}
                        onRefresh={onRefresh}
                    />
                }
            >
                <MaxWidthWrapper paddingHorizontal={'s16'}>
                    <ScreenNavBar
                        title={
                            hasSocialWallet
                                ? t('wallet:social_wallet')
                                : t('product_wallet', { product: activeProductName })
                        }
                        subTitle={sharedWalletSubtitle}
                        closeIconMode={params.mode === 'stack' ? 'close' : 'none'}
                        titleVariant={'titleMedium2'}
                        height={48}
                        style={styles.screenNavBar}
                    />
                    <Column paddingTop={'s10'}>
                        <WalletCards cards={cards} />
                        <WalletActionButtons product={activeProduct} />
                        <SecondaryWalletButton activeProduct={activeProduct} />
                        <Links links={[...links]} />
                        <AccountFooter />
                    </Column>
                </MaxWidthWrapper>
            </ScrollView>
        </Screen>
    );
};

const styles = StyleSheet.create({
    screenNavBar: {
        marginBottom: 8,
    },
});
